export { carbonConvert } from "common/utils/units/units";
import { BlockColors, BlockRange } from "./blocks";
import { BlockResponse } from "protos/portal/spatial";
import { CarbonUnit, UnitNumberType } from "./units/units";
import {
  DailyMetricResponse,
  EntryTimingGrade,
  JobMetricResponse,
} from "protos/portal/metrics";
import { DateTime } from "luxon";
import { i18n as I18n, TFunction } from "i18next";
import { isUndefined } from "./identity";
import { JigsawSpatialMetricBlock } from "protos/portal/jigsaw";
import { KeysExtend } from "../../../common/src/utils/types";
import { TimeUnits } from "convert-units/definitions/time";

export enum MetricValidation {
  VALIDATED = "validated",
  PENDING = "pending",
  INTERNAL = "internal",
}

export enum CycleSlot {
  ALTITUDE = "metric-altitude",
  AREA = "metric-area",
  DENSITY = "metric-density",
  DISTANCE = "metric-distance",
  SHOOT = "metric-shoot",
  LIFETIME = "metric-lifetime",
  SIZE = "metric-size",
  SPEED = "metric-speed",
  COVERAGE = "metric-coverage",
  /**
   * for time values with all time units available
   **/
  TIME = "metric-time",
  /**
   * for time values with options limited to hours, minutes, and seconds
   **/
  TIME_LIMITED = "metric-time-limited",
  COMPONENT_ENCODERS = "encoders",
  STATUS_AREA = "status-area",
  STATUS_TIME = "status-time",
}

export type CertifiedMetricBooleanId = KeysExtend<DailyMetricResponse, boolean>;
// Protobuf enum fields. KeysExtend can't pick these out: a numeric enum and a
// plain number are mutually assignable, so it would match every number field.
export type CertifiedMetricEnumId = Extract<
  keyof DailyMetricResponse,
  "entryTimingGrade"
>;
export type CertifiedMetricListId =
  | KeysExtend<DailyMetricResponse, any[]>
  | "weedMetricsByCategory";
export type CertifiedMetricNumberId = KeysExtend<DailyMetricResponse, number>;
export type CertifiedMetricStringId = KeysExtend<DailyMetricResponse, string>;

export type SpatialMetricBooleanId =
  | "coverage"
  | "estopped"
  | "interlock"
  | "laserKey"
  | "lifted"
  | "thinning"
  | "waterProtect"
  | "weeding";
export type SpatialMetricDateId = "dateTime";
export type SpatialMetricNumberId =
  | "altitude"
  | "averageCropSize"
  | "averageWeedSize"
  | "avgTargetedReqLaserTime"
  | "broadleaf"
  | "cropDensity"
  | "cropsKept"
  | "cropsKilled"
  | "cropsMissed"
  | "cropsSkipped"
  | "grass"
  | "offshoot"
  | "overallEfficiency"
  | "purslane"
  | "percentBanded"
  | "remainingCropDensity"
  | "speed"
  | "speedTargetSmoothed"
  | "speedTravel"
  | "thinningEfficiency"
  | "totalCropsValid"
  | "totalWeeds"
  | "totalWeedsInBand"
  | "weedDensity"
  | "weedingEfficiency"
  | "weedsKilled"
  | "weedsMissed"
  | "weedsSkipped";
export type SpatialMetricStringId = CertifiedMetricStringId;

export interface MetricBase<ID extends string> {
  canAverage: boolean;
  canTotal: boolean;
  /**
   * only applies to numeric metrics, but is here to make types slightly easier
   **/
  cycleSlot?: CycleSlot;
  id: ID;
  // translation key suffix for the metric's name/help, when it differs from
  // id (e.g. a metric renamed to match a new API field keeps its old key)
  i18nId?: string;
  validation: MetricValidation;
}

export interface SpatialMetricBase<ID extends string, T>
  extends MetricBase<ID> {
  canMap: boolean;
  popup?: SpatialMetric[];
  // Set on dynamic per-category jigsaw metrics (see makeWeedCategoryMetric /
  // makeCropCategoryMetric): the plant category the metric reads from, and
  // which category system it belongs to.
  categoryId?: string;
  categoryKind?: "weed" | "crop";
  // Pre-resolved display name; when set, takes precedence over the
  // `utils.metrics.spatial.metrics.${id}` translation. Used for per-category
  // metrics whose names are category names, not static keys.
  displayName?: string;
  getValue: (block: BlockResponse) => T | undefined;
  getJigsawValue: (value: JigsawSpatialMetricBlock) => T | undefined; // Interface to be updated when hookingup jigsaw API
  colors: BlockColors;
  minY?: number;
  maxY?: number;
  fill?: boolean;
  getColor: (
    value: T | undefined,
    range: BlockRange,
    isRelative?: boolean
  ) => string;
  renderWhenNotCountable?: boolean;
}

export type SpatialMetricDateTime = SpatialMetricBase<
  SpatialMetricDateId,
  Date
> & {
  type: "dateTime";
  cycleSlot?: CycleSlot;
  units: TimeUnits;
};

export interface MetricStringBase {
  type: "string";
  getValue: (metrics: DailyMetricResponse) => string | undefined;
}

export type MetricString = MetricBase<CertifiedMetricStringId> &
  MetricStringBase;
export type SpatialMetricString = SpatialMetricBase<
  SpatialMetricStringId,
  string
> &
  Omit<MetricStringBase, "getValue">;

export interface MetricBooleanBase {
  type: "boolean";
  i18nKey?: string;
  getValue: (metrics: DailyMetricResponse) => boolean | undefined;
}

export type MetricBoolean = MetricBase<CertifiedMetricBooleanId> &
  MetricBooleanBase;
export type SpatialMetricBoolean = SpatialMetricBase<
  SpatialMetricBooleanId,
  boolean
> &
  Omit<MetricBooleanBase, "getValue">;

export interface MetricNumberBase {
  type: "number";
  units: CarbonUnit;
  getValue: (metrics: DailyMetricResponse) => number | undefined;
}

export type MetricNumber = MetricBase<CertifiedMetricNumberId> &
  MetricNumberBase;
export type SpatialMetricNumber = SpatialMetricBase<
  SpatialMetricNumberId,
  number
> &
  Omit<MetricNumberBase, "getValue">;

export interface MetricEnumBase {
  type: "enum";
  // Protobuf enums are numbers on the wire, so the raw value is what the
  // aggregates compare and `formatValue` is what the table displays.
  getValue: (metrics: DailyMetricResponse) => number | undefined;
  formatValue: (value: number | undefined, t: TFunction) => string | undefined;
}

export type MetricEnum = MetricBase<CertifiedMetricEnumId> & MetricEnumBase;

export type SpatialMetricDiscreteId = "entryTiming";

export type SpatialMetricDiscrete = SpatialMetricBase<
  SpatialMetricDiscreteId,
  EntryTimingGrade
> & {
  type: "discrete";
};

export type MetricList = MetricBase<CertifiedMetricListId> & {
  type: "list";
  getValue: (
    metrics: DailyMetricResponse | JobMetricResponse
  ) => { id: string; name: string; percent?: number }[] | undefined;
};

export type Metric =
  | MetricBoolean
  | MetricEnum
  | MetricList
  | MetricNumber
  | MetricString;

export type SpatialMetric =
  | SpatialMetricDateTime
  | SpatialMetricBoolean
  | SpatialMetricDiscrete
  | SpatialMetricNumber;

export type AnyMetric = Metric | SpatialMetric;

export const isCount = (
  unit: MetricNumberBase["units"] | undefined
): unit is UnitNumberType.COUNT => UnitNumberType.COUNT === unit;

export const isPercent = (
  unit: MetricNumberBase["units"] | undefined
): unit is UnitNumberType.PERCENT => UnitNumberType.PERCENT === unit;

export const isSpatialMetric = (metric: AnyMetric): metric is SpatialMetric =>
  "canMap" in metric;

export const isMetricString = (metric: AnyMetric): metric is MetricString =>
  metric.type === "string" && !isSpatialMetric(metric);

export const isMetricBoolean = (metric: AnyMetric): metric is MetricBoolean =>
  metric.type === "boolean" && !isSpatialMetric(metric);

export const isMetricNumber = (metric: AnyMetric): metric is MetricNumber =>
  metric.type === "number" && !isSpatialMetric(metric);

export const isSpatialMetricDateTime = (
  metric: AnyMetric
): metric is SpatialMetricDateTime =>
  metric.type === "dateTime" && isSpatialMetric(metric);

export const isSpatialMetricBoolean = (
  metric: AnyMetric
): metric is SpatialMetricBoolean =>
  metric.type === "boolean" && isSpatialMetric(metric);

export const isSpatialMetricNumber = (
  metric: AnyMetric
): metric is SpatialMetricNumber =>
  metric.type === "number" && isSpatialMetric(metric);

export const isSpatialMetricDiscrete = (
  metric: AnyMetric
): metric is SpatialMetricDiscrete =>
  metric.type === "discrete" && isSpatialMetric(metric);

export const getSpatialMetricName = (
  t: TFunction,
  metric: SpatialMetric
): string =>
  metric.displayName ??
  // carbon.actions.compareKeys.ignoreDynamic
  t(`utils.metrics.spatial.metrics.${metric.i18nId ?? metric.id}`);

export const unitsToI18nKey = (units: CarbonUnit | UnitNumberType): string => {
  if (isCount(units)) {
    return "";
  } else if (isPercent(units)) {
    return "utils.units.%";
  } else {
    return units;
  }
};

export const formatTime = (i18n: I18n, value?: number | undefined): string => {
  if (isUndefined(value) || value === 0) {
    return "";
  }
  return DateTime.fromMillis(value).toLocaleString(
    {
      hour12: true,
      hour: "numeric",
      minute: "2-digit",
      weekday: "long",
      month: "long",
      day: "numeric",
    },
    { locale: i18n.language }
  );
};
